import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Bell, Check, CheckCheck, Trash2, MessageSquare, 
  Gavel, ShoppingBag, Star, AlertCircle, Heart, TrendingUp,
  Settings, X, Calendar, DollarSign, Tag, Eye, Search,
  Package, Clock, Filter, Inbox, Megaphone, RefreshCw, Loader2, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import axios from '../lib/axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { DashboardContainer, PageHeader, FilterChips } from '../components/DashboardLayout';
import NotificationSettings from '../components/NotificationSettings';
import { API, useAuth, useLanguage, useNotifications } from '../App';

// Notification type configurations with colors and labels
const notificationTypeConfig = {
  message: {
    icon: MessageSquare,
    color: 'blue',
    bgColor: 'bg-blue-500/10',
    textColor: 'text-blue-500',
    borderColor: 'border-blue-500/30',
    label: 'Message',
    badgeColor: 'bg-blue-500'
  },
  offer: {
    icon: ShoppingBag,
    color: 'emerald',
    bgColor: 'bg-emerald-500/10',
    textColor: 'text-emerald-500',
    borderColor: 'border-emerald-500/30',
    label: 'Offer',
    badgeColor: 'bg-emerald-500'
  },
  outbid: {
    icon: Gavel,
    color: 'orange',
    bgColor: 'bg-orange-500/10',
    textColor: 'text-orange-500',
    borderColor: 'border-orange-500/30',
    label: 'Outbid',
    badgeColor: 'bg-orange-500'
  },
  auction_won: {
    icon: TrendingUp,
    color: 'green',
    bgColor: 'bg-green-500/10',
    textColor: 'text-green-500',
    borderColor: 'border-green-500/30',
    label: 'Won!',
    badgeColor: 'bg-green-500'
  },
  auction_ended: {
    icon: Gavel,
    color: 'purple',
    bgColor: 'bg-purple-500/10',
    textColor: 'text-purple-500',
    borderColor: 'border-purple-500/30',
    label: 'Ended',
    badgeColor: 'bg-purple-500'
  },
  review: {
    icon: Star,
    color: 'yellow',
    bgColor: 'bg-yellow-500/10',
    textColor: 'text-yellow-500',
    borderColor: 'border-yellow-500/30',
    label: 'Review',
    badgeColor: 'bg-yellow-500'
  },
  sale: {
    icon: Package,
    color: 'green',
    bgColor: 'bg-green-500/10',
    textColor: 'text-green-500',
    borderColor: 'border-green-500/30',
    label: 'Sold',
    badgeColor: 'bg-green-500'
  },
  favorite: {
    icon: Heart,
    color: 'red',
    bgColor: 'bg-red-500/10',
    textColor: 'text-red-500',
    borderColor: 'border-red-500/30',
    label: 'Favorite',
    badgeColor: 'bg-red-500'
  },
  favorite_sold: {
    icon: Heart,
    color: 'red',
    bgColor: 'bg-red-500/10',
    textColor: 'text-red-500',
    borderColor: 'border-red-500/30',
    label: 'Sold',
    badgeColor: 'bg-red-500'
  },
  favorite_reserved: {
    icon: Heart,
    color: 'amber',
    bgColor: 'bg-amber-500/10',
    textColor: 'text-amber-500',
    borderColor: 'border-amber-500/30',
    label: 'Reserved',
    badgeColor: 'bg-amber-500'
  },
  favorite_price_change: {
    icon: DollarSign,
    color: 'green',
    bgColor: 'bg-green-500/10',
    textColor: 'text-green-500',
    borderColor: 'border-green-500/30',
    label: 'Price Drop',
    badgeColor: 'bg-green-500'
  },
  saved_search: {
    icon: Search,
    color: 'indigo',
    bgColor: 'bg-indigo-500/10',
    textColor: 'text-indigo-500',
    borderColor: 'border-indigo-500/30',
    label: 'Match',
    badgeColor: 'bg-indigo-500'
  },
  event_reminder: {
    icon: Calendar,
    color: 'purple',
    bgColor: 'bg-purple-500/10',
    textColor: 'text-purple-500',
    borderColor: 'border-purple-500/30',
    label: 'Event',
    badgeColor: 'bg-purple-500'
  },
  listing_expiry_warning: {
    icon: Clock,
    color: 'amber',
    bgColor: 'bg-amber-500/10',
    textColor: 'text-amber-500',
    borderColor: 'border-amber-500/30',
    label: 'Verloopt',
    badgeColor: 'bg-amber-500'
  },
  listing_expired: {
    icon: AlertCircle,
    color: 'orange',
    bgColor: 'bg-orange-500/10',
    textColor: 'text-orange-500',
    borderColor: 'border-orange-500/30',
    label: 'Verlopen',
    badgeColor: 'bg-orange-500'
  },
  listing_archived: {
    icon: Package,
    color: 'gray',
    bgColor: 'bg-gray-500/10',
    textColor: 'text-gray-500',
    borderColor: 'border-gray-500/30',
    label: 'Gearchiveerd',
    badgeColor: 'bg-gray-500'
  },
  listing_reactivated: {
    icon: Check,
    color: 'green',
    bgColor: 'bg-green-500/10',
    textColor: 'text-green-500',
    borderColor: 'border-green-500/30',
    label: 'Actief',
    badgeColor: 'bg-green-500'
  },
  system: {
    icon: AlertCircle,
    color: 'gray',
    bgColor: 'bg-gray-500/10',
    textColor: 'text-gray-500',
    borderColor: 'border-gray-500/30',
    label: 'System',
    badgeColor: 'bg-gray-500'
  },
  announcement: {
    icon: Megaphone,
    color: 'blue',
    bgColor: 'bg-blue-500/10',
    textColor: 'text-blue-500',
    borderColor: 'border-blue-500/30',
    label: 'Announcement',
    badgeColor: 'bg-blue-500'
  },
  default: {
    icon: Bell,
    color: 'primary',
    bgColor: 'bg-primary/10',
    textColor: 'text-primary',
    borderColor: 'border-primary/30',
    label: 'Alert',
    badgeColor: 'bg-primary'
  }
};

const NotificationsPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { 
    notifications, 
    loading, 
    markAsRead: contextMarkAsRead, 
    markAllAsRead: contextMarkAllAsRead, 
    deleteNotification: contextDeleteNotification,
    refresh 
  } = useNotifications();
  const [filter, setFilter] = useState('all'); // all, unread, messages, offers, saved_search, etc.
  const [showSettings, setShowSettings] = useState(false);
  const [animatedIds, setAnimatedIds] = useState(new Set()); // Track newly added notifications for animation
  const prevNotificationIdsRef = useRef(new Set());
  const [extendingId, setExtendingId] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [extendedIds, setExtendedIds] = useState(new Set()); // Track extended listings
  const [expandedMessages, setExpandedMessages] = useState(new Set()); // Track expanded notification messages

  // Toggle expanded state for a notification message
  const toggleExpandMessage = (notificationId) => {
    setExpandedMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
      } else {
        newSet.add(notificationId);
      }
      return newSet;
    });
  };

  // Check if a message is long enough to need expansion (more than ~100 chars)
  const isLongMessage = (message) => {
    if (!message) return false;
    return message.length > 120;
  };

  // Extend listing handler
  const handleExtendListing = async (notification) => {
    const listingId = notification.action_listing_id;
    if (!listingId) return;
    
    setExtendingId(notification.id);
    try {
      const response = await axios.post(`${API}/listings/${listingId}/extend`);
      
      if (response.data.success) {
        // Show confetti celebration
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
        
        toast.success(
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            <span>{response.data.message}</span>
          </div>,
          { duration: 5000 }
        );
        
        // Mark this notification as extended
        setExtendedIds(prev => new Set([...prev, notification.id]));
        
        // Mark notification as read
        await contextMarkAsRead(notification.id);
        
        // Refresh notifications
        refresh();
      }
    } catch (error) {
      console.error('Error extending listing:', error);
      toast.error(error.response?.data?.detail || 'Kon advertentie niet verlengen');
    } finally {
      setExtendingId(null);
    }
  };

  // Track new notifications for animation
  useEffect(() => {
    const currentIds = new Set(notifications.map(n => n.id));
    const prevIds = prevNotificationIdsRef.current;
    
    // Find newly added notifications
    const newIds = [...currentIds].filter(id => !prevIds.has(id));
    
    if (newIds.length > 0 && prevIds.size > 0) {
      // Only animate if this isn't the initial load
      setAnimatedIds(new Set(newIds));
      
      // Remove animation class after animation completes
      const timeout = setTimeout(() => {
        setAnimatedIds(new Set());
      }, 600); // Match animation duration
      
      return () => clearTimeout(timeout);
    }
    
    prevNotificationIdsRef.current = currentIds;
  }, [notifications]);

  // Body scroll lock when settings modal is open
  useEffect(() => {
    if (showSettings) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showSettings]);

  const markAsRead = async (notificationId) => {
    try {
      await contextMarkAsRead(notificationId);
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await contextMarkAllAsRead();
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await contextDeleteNotification(notificationId);
      toast.success('Notification deleted');
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  const handleViewDetails = async (notification) => {
    if (!notification.read) {
      await markAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const getTypeConfig = (type) => {
    return notificationTypeConfig[type] || notificationTypeConfig.default;
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  // Filter options for quick access with counts
  const getFilterCount = (key) => {
    if (key === 'all') return notifications.length;
    if (key === 'unread') return notifications.filter(n => !n.read).length;
    if (key === 'favorite') {
      return notifications.filter(n => 
        n.type === 'favorite' || n.type === 'favorite_sold' || 
        n.type === 'favorite_reserved' || n.type === 'favorite_price_change'
      ).length;
    }
    return notifications.filter(n => n.type === key).length;
  };
  
  const filterOptions = [
    { key: 'all', label: 'All', icon: Inbox },
    { key: 'unread', label: 'Unread', icon: Bell },
    { key: 'offer', label: 'Offers', icon: ShoppingBag },
  ];

  const getFilteredNotifications = () => {
    if (filter === 'all') return notifications;
    if (filter === 'unread') return notifications.filter(n => !n.read);
    if (filter === 'favorite') {
      return notifications.filter(n => 
        n.type === 'favorite' || n.type === 'favorite_sold' || 
        n.type === 'favorite_reserved' || n.type === 'favorite_price_change'
      );
    }
    return notifications.filter(n => n.type === filter);
  };

  const filteredNotifications = getFilteredNotifications();
  const unreadCount = notifications.filter(n => !n.read).length;

  // Group notifications by date
  const groupNotificationsByDate = (notifs) => {
    const groups = {};
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    notifs.forEach(n => {
      const date = new Date(n.created_at);
      let key;
      
      if (date.toDateString() === today.toDateString()) {
        key = 'Today';
      } else if (date.toDateString() === yesterday.toDateString()) {
        key = 'Yesterday';
      } else if (date > new Date(today.setDate(today.getDate() - 7))) {
        key = 'This Week';
      } else {
        key = 'Earlier';
      }
      
      if (!groups[key]) groups[key] = [];
      groups[key].push(n);
    });
    
    return groups;
  };

  const groupedNotifications = groupNotificationsByDate(filteredNotifications);

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <DashboardContainer>
          <div className="py-16 text-center">
            <Bell className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h1 className="text-2xl font-bold mb-4">Sign in to view notifications</h1>
            <p className="text-muted-foreground mb-6">Stay updated on your listings, messages, and auctions</p>
            <Link to="/auth" className="btn-primary inline-block">
              Sign In
            </Link>
          </div>
        </DashboardContainer>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Confetti Animation Overlay */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-[150] overflow-hidden">
          {/* Generate confetti particles */}
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-bounce"
              style={{
                left: `${Math.random() * 100}%`,
                top: `-10px`,
                animationDelay: `${Math.random() * 0.5}s`,
                animationDuration: `${1 + Math.random() * 2}s`,
              }}
            >
              <div
                className={`w-3 h-3 rotate-45 ${
                  ['bg-amber-400', 'bg-orange-400', 'bg-green-400', 'bg-blue-400', 'bg-purple-400', 'bg-pink-400'][
                    Math.floor(Math.random() * 6)
                  ]
                }`}
                style={{
                  animation: `fall ${2 + Math.random() * 2}s linear forwards`,
                }}
              />
            </div>
          ))}
          <style>{`
            @keyframes fall {
              0% { transform: translateY(0) rotate(0deg); opacity: 1; }
              100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
            }
          `}</style>
        </div>
      )}
      
      <DashboardContainer>
        {/* Standardized Header */}
        <PageHeader pageKey="notifications">
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 bg-primary text-white text-xs font-bold rounded-full">
              {unreadCount} unread
            </span>
          )}
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-2 rounded-xl hover:bg-secondary transition-colors"
            title="Notification settings"
          >
            <Settings className="w-5 h-5" />
          </button>
          {unreadCount > 0 && (
            <button 
              onClick={markAllAsRead}
              className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-2 text-sm text-primary hover:bg-primary/10 rounded-lg transition-colors whitespace-nowrap"
            >
              <CheckCheck className="w-4 h-4" />
              <span className="hidden sm:inline">Mark all read</span>
            </button>
          )}
        </PageHeader>

        {/* Filter Chips */}
        <FilterChips
          options={filterOptions.map(opt => ({
            ...opt,
            count: getFilterCount(opt.key)
          }))}
          activeFilter={filter}
          onFilterChange={setFilter}
        />

        {/* Loading State */}
        {loading && (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="card p-4 animate-pulse">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-muted rounded-xl"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-muted rounded w-1/2"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredNotifications.length === 0 && (
          <div className="text-center py-16">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-secondary flex items-center justify-center">
              <Bell className="w-12 h-12 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-semibold mb-2">
              {filter === 'unread' ? 'All caught up!' : filter !== 'all' ? `No ${filter.replace('_', ' ')} notifications` : 'No notifications yet'}
            </h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              {filter === 'unread' 
                ? "You've read all your notifications" 
                : "You'll receive notifications for messages, bids, saved search matches, and more"}
            </p>
          </div>
        )}

        {/* Grouped Notifications List */}
        {!loading && filteredNotifications.length > 0 && (
          <div className="grid gap-6">
            {Object.entries(groupedNotifications).map(([dateGroup, notifs]) => (
              <div key={dateGroup}>
                <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {dateGroup}
                </h3>
                <div className="grid gap-3">
                  {notifs.map((notification) => {
                    const config = getTypeConfig(notification.type);
                    const Icon = config.icon;
                    
                    return (
                      <div
                        key={notification.id}
                        className={`relative rounded-2xl border-2 transition-all hover:shadow-lg ${
                          !notification.read 
                            ? `${config.bgColor} ${config.borderColor} border-l-4` 
                            : 'bg-card border-border hover:bg-secondary/30'
                        } ${
                          animatedIds.has(notification.id) 
                            ? 'animate-in fade-in slide-in-from-top-2 duration-500' 
                            : ''
                        }`}
                        data-testid={`notification-${notification.id}`}
                      >
                        <div className="p-5">
                          <div className="flex items-start gap-3">
                            {/* Icon with type-specific styling */}
                            <div className={`p-2.5 rounded-xl flex-shrink-0 ${
                              !notification.read ? config.bgColor : 'bg-secondary'
                            }`}>
                              <Icon className={`w-5 h-5 ${config.textColor}`} />
                            </div>
                            
                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  {/* Type Badge */}
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold text-white ${config.badgeColor}`}>
                                    {config.label}
                                  </span>
                                  {!notification.read && (
                                    <span className="px-1.5 py-0.5 bg-primary text-white text-xs font-bold rounded animate-pulse">
                                      NEW
                                    </span>
                                  )}
                                </div>
                                <span className={`text-xs whitespace-nowrap ${
                                  !notification.read ? config.textColor + ' font-medium' : 'text-muted-foreground'
                                }`}>
                                  {formatTimeAgo(notification.created_at)}
                                </span>
                              </div>
                              
                              <p className={`text-sm font-medium mb-1 ${
                                !notification.read ? 'text-foreground' : 'text-muted-foreground'
                              }`}>
                                {notification.title}
                              </p>
                              
                              {/* Message with expandable text for long messages */}
                              <div className="relative">
                                <p className={`text-sm ${
                                  !notification.read ? 'text-foreground/80' : 'text-muted-foreground/80'
                                } ${
                                  !expandedMessages.has(notification.id) && isLongMessage(notification.message) 
                                    ? 'line-clamp-2' 
                                    : ''
                                }`}>
                                  {notification.message}
                                </p>
                                
                                {/* Read more / Read less toggle for long messages */}
                                {isLongMessage(notification.message) && (
                                  <button
                                    onClick={() => toggleExpandMessage(notification.id)}
                                    className={`text-xs font-medium mt-1 transition-colors ${
                                      !notification.read 
                                        ? `${config.textColor} hover:opacity-80` 
                                        : 'text-primary hover:text-primary/80'
                                    }`}
                                  >
                                    {expandedMessages.has(notification.id) 
                                      ? (t('read_less') || 'Lees minder ▲')
                                      : (t('read_more') || 'Lees meer ▼')
                                    }
                                  </button>
                                )}
                              </div>
                              
                              {/* Image preview for saved search matches */}
                              {notification.data?.listing_image && (
                                <div className="mt-2">
                                  <img 
                                    src={notification.data.listing_image} 
                                    alt="" 
                                    className="w-16 h-16 rounded-lg object-cover border border-border"
                                  />
                                </div>
                              )}
                              
                              {/* Actions */}
                              <div className="flex items-center gap-3 mt-3 flex-wrap">
                                {/* Special action button for listing expiry notifications */}
                                {(notification.type === 'listing_expiry_warning' || notification.type === 'listing_expired') && 
                                 notification.action_listing_id && (
                                  extendedIds.has(notification.id) ? (
                                    // Already extended - show success state
                                    <div className="px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 text-sm font-medium rounded-xl flex items-center gap-2">
                                      <Check className="w-4 h-4" />
                                      {t('reactivated')}
                                    </div>
                                  ) : (
                                    // Not yet extended - show action button
                                    <button 
                                      onClick={() => handleExtendListing(notification)}
                                      disabled={extendingId === notification.id}
                                      className="px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-medium rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all flex items-center gap-2 disabled:opacity-50"
                                    >
                                      {extendingId === notification.id ? (
                                        <>
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                          {t('extending')}
                                        </>
                                      ) : (
                                        <>
                                          <RefreshCw className="w-4 h-4" />
                                          {t('yes_still_available')}
                                        </>
                                      )}
                                    </button>
                                  )
                                )}
                                
                                {/* View Details - only show if there's a valid link and not for expiry notifications */}
                                {notification.link && 
                                 notification.link !== '#' && 
                                 !(notification.type === 'listing_expiry_warning' || notification.type === 'listing_expired') && (
                                  <button 
                                    onClick={() => handleViewDetails(notification)}
                                    className={`text-sm font-medium transition-colors flex items-center gap-1 ${
                                      !notification.read 
                                        ? `${config.textColor} hover:opacity-80` 
                                        : 'text-muted-foreground hover:text-foreground'
                                    }`}
                                  >
                                    <Eye className="w-3.5 h-3.5" />
                                    View details
                                  </button>
                                )}
                                
                                {!notification.read && (
                                  <button
                                    onClick={() => markAsRead(notification.id)}
                                    className="text-sm text-muted-foreground hover:text-green-500 transition-colors flex items-center gap-1"
                                  >
                                    <Check className="w-3.5 h-3.5" />
                                    Mark read
                                  </button>
                                )}
                                
                                <button
                                  onClick={() => deleteNotification(notification.id)}
                                  className="text-sm text-muted-foreground hover:text-red-500 transition-colors flex items-center gap-1 ml-auto"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </DashboardContainer>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b p-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Notification Settings
              </h2>
              <button 
                onClick={() => setShowSettings(false)}
                className="p-2 hover:bg-secondary rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <NotificationSettings user={user} onClose={() => setShowSettings(false)} />
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default NotificationsPage;
